import dynamic from 'next/dynamic';
import { ImageProps } from 'next/image';

import { Link } from '@natu/next-link';
import { cn } from '@natu/utils/cn';
import { PredefinedGAEvents } from '@natu/utils/events';
import { getSlugWithDash } from '@natu/utils/getSlugWithDash';

const ArticleButton = dynamic(() => import('./ArticleButton').then(mod => mod.ArticleButton));

const ResponsiveImage = dynamic(() =>
  import('@natu/ui/ResponsiveImage').then(mod => mod.ResponsiveImage),
);

export interface ArticleProps {
  className?: string;
  label?: string;
  title?: string;
  description?: string | null;
  image?: ImageProps;
  url?: string | null;
  showButton?: boolean;
  buttonEvent?: PredefinedGAEvents;
  descriptiveText?: string;
}

const Article = ({
  className,
  label,
  title,
  description,
  image,
  url,
  showButton = true,
  buttonEvent,
  descriptiveText,
}: ArticleProps) => {
  if (!url) return null;

  const href = getSlugWithDash(url);

  return (
    <div className={cn(className, 'flex h-full flex-col')}>
      {image?.src && (
        <Link
          className="group relative mb-6 block aspect-[311/223] shrink-0 overflow-hidden rounded-lg"
          href={href}
          {...(descriptiveText && { 'aria-label': descriptiveText })}
        >
          <ResponsiveImage
            className="h-full w-full object-cover transition-all duration-200 group-hover:-translate-x-3 group-hover:scale-[1.07]"
            {...image}
          />
        </Link>
      )}
      {label && <p className="mb-4 flex items-center gap-2 text-sm text-gray-400">{label}</p>}
      <div className="flex h-full flex-col justify-between gap-y-6">
        <div className="flex h-full flex-col justify-between gap-y-6">
          <Link href={href}>
            <p className="line-clamp-3 text-lg font-bold text-gray-800">{title}</p>
          </Link>
          {description && <p className="line-clamp-3 text-base text-gray-600">{description}</p>}
        </div>
        {showButton && (
          <ArticleButton
            event={buttonEvent}
            href={href}
            label="READ MORE"
            descriptiveText={descriptiveText}
          />
        )}
      </div>
    </div>
  );
};

export { Article, ArticleButton };
